import React from 'react';
import { CheckCircle, Calendar, Target, TrendingUp } from 'lucide-react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { Progress } from '@/components/ui/progress';
import MovableCard from '@/components/ui/MovableCards';
import { cn } from '@/lib/utils';
import { useGoalFilteredTasks } from '@/hooks/tasks/use-goal-filtered-tasks';
import { getProgressColors } from '@/utils/goal-progress-colors';
import { DateRangeOption } from '@/hooks/tasks/types/task-operations-types';

// Animation variants for motion components (used by other goal components)
export const cardVariants = {
  initial: { opacity: 0, scale: 0.9, y: 10 },
  animate: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1.0]
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.9, 
    y: -10,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1.0]
    }
  }
};

export interface Goal {
  id: string;
  title: string;
  description?: string;
  progress: number;
  dueDate?: string;
  status?: string;
  category?: string;
  isCompleted?: boolean;
  order?: number;
  projectTitle?: string;
  projectId?: string;
  projectCoverImage?: string;
}

interface GoalCardProps {
  goal: Goal;
  onClick: (goalId: string) => void;
  isDragging?: boolean;
  dragHandleRef?: (element: HTMLElement | null) => void;
  dragListeners?: SyntheticListenerMap | undefined;
  dragAttributes?: DraggableAttributes;
}

const GoalCard: React.FC<GoalCardProps> = ({ 
  goal, 
  onClick, 
  isDragging = false,
  dragHandleRef,
  dragListeners,
  dragAttributes
}) => {
  const { id, title, progress: dbProgress, dueDate, projectTitle, projectCoverImage } = goal;
  
  // Get ALL tasks for this goal (bypasses date filtering for overall progress)
  const { activeTasks, completedTasks } = useGoalFilteredTasks(id);
  
  // Calculate progress based on ALL task completion (not date-filtered)
  const totalTasks = activeTasks.length + completedTasks.length;
  const completedTasksCount = completedTasks.length;
  
  // Calculate progress based on task completion (single source of truth)
  const calculatedProgress = totalTasks > 0 ? Math.round((completedTasksCount / totalTasks) * 100) : 0;
  
  // Use calculated progress as the primary source, fall back to database progress if no tasks
  const displayProgress = totalTasks > 0 ? calculatedProgress : dbProgress;

  // Create the task completion text
  const taskCompletionText = totalTasks > 0 
    ? `${completedTasksCount} of ${totalTasks} tasks completed`
    : "No tasks yet";
  
  // Debug log removed to prevent performance issues and console spam
  // console.log(`GoalCard ${id}: Using ALL tasks - ${completedTasksCount}/${totalTasks} = ${displayProgress}%`);

  // Get dynamic colors based on progress
  const progressColors = getProgressColors(displayProgress);

  // Format due date
  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} days`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} days`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const isOverdue = dueDate && new Date(dueDate) < new Date();
  const isCompleted = displayProgress === 100;

  return (
    <MovableCard
      onClick={() => onClick(id)}
      isDragging={isDragging}
      dragHandleRef={dragHandleRef}
      dragListeners={dragListeners}
      dragAttributes={dragAttributes}
      showMoveButton={!!dragHandleRef}
      moveButtonVariant="glass"
      moveButtonSize="sm"
      moveButtonIconType="vertical"
      className="group hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
      backgroundImage={projectCoverImage}
    >
      {/* Goal Content - with right padding to avoid overlap with move button */}
      <div className="flex flex-col justify-between h-full space-y-5">
        {/* Header Section */}
        <div>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3">
                <div className={cn(
                  "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200",
                  isCompleted 
                    ? "bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/25" 
                    : `bg-gradient-to-br ${progressColors.cardGradient} shadow-lg ${progressColors.shadowClass}`
                )}>
                  {isCompleted ? (
                    <CheckCircle className="text-white" size={20} />
                  ) : (
                    <Target className="text-white" size={20} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {/* Project Title (now big text, displayed first) */}
                  {projectTitle && (
                    <h3 className={cn(
                      "font-semibold text-lg leading-tight transition-colors duration-200",
                      isCompleted 
                        ? "text-green-300 dark:text-green-300 line-through decoration-2" 
                        : `text-white dark:text-white ${progressColors.hoverColor}`
                    )}>
                      {projectTitle}
                    </h3>
                  )}
                  
                  {/* Goal Title (now small text, displayed second) */}
                  <h4 className="text-xs font-medium text-gray-300 dark:text-gray-300 mt-0.5">
                    {title}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Progress Section - Now at the bottom */}
        <div className="space-y-3 mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-300 dark:text-gray-300">{taskCompletionText}</span>
              {!isCompleted && completedTasksCount > 0 && (
                <TrendingUp className={cn(
                  "transition-colors duration-200",
                  projectCoverImage ? "text-white" : progressColors.cardColor
                )} size={14} />
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-sm font-bold transition-colors duration-200",
                isCompleted 
                  ? "text-green-300 dark:text-green-300" 
                  : "text-white dark:text-white"
              )}>
                {displayProgress}%
              </span>
              {displayProgress > 0 && displayProgress < 100 && (
                <div className={cn(
                  "w-2 h-2 rounded-full animate-pulse",
                  `bg-gradient-to-r ${progressColors.progressGradient}`
                )} />
              )}
            </div>
          </div>
          
          <div className="relative">
            <Progress 
              value={displayProgress} 
              className="h-3 bg-gray-200/60 dark:bg-gray-700/60 rounded-full overflow-hidden shadow-inner" 
              indicatorClassName={cn(
                `bg-gradient-to-r ${progressColors.progressGradient} transition-all duration-500 ease-out`,
                displayProgress > 0 && "shadow-lg"
              )}
            />
            {/* Subtle glow effect for higher progress */}
            {displayProgress > 50 && (
              <div className={cn(
                "absolute inset-0 rounded-full opacity-20 blur-sm",
                `bg-gradient-to-r ${progressColors.progressGradient}`
              )} />
            )}
          </div>
        </div>
        
        {/* Due Date Section - Moved above progress section */}
        {dueDate && (
          <div className={cn(
            "flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors duration-200",
            isOverdue && !isCompleted
              ? "bg-red-500/20 dark:bg-red-950/40 text-red-100 dark:text-red-300 border border-red-500/30 dark:border-red-800/30"
              : isCompleted
              ? "bg-green-500/20 dark:bg-green-950/40 text-green-100 dark:text-green-300 border border-green-500/30 dark:border-green-800/30"
              : "bg-blue-500/20 dark:bg-blue-950/40 text-blue-100 dark:text-blue-300 border border-blue-500/30 dark:border-blue-800/30"
          )}>
            <Calendar size={14} className="flex-shrink-0" />
            <span className="font-medium">
              {formatDueDate(dueDate)}
            </span>
          </div>
        )}
      </div>
    </MovableCard>
  );
};

export default GoalCard;
